import React, { useState, useEffect } from 'react';
import {
    CheckCircle2,
    Circle,
    Calendar,
    User,
    MessageSquare,
    ChevronRight,
    Plus,
    Loader2,
    Trash2,
    X,
    Send
} from 'lucide-react';
import { useCelebration, CelebrationOverlay } from './CelebrationEffects';
import {
    fetchCaseTasks,
    createTask,
    updateTask,
    deleteTask,
    fetchTaskComments,
    addTaskComment
} from '../api/tasks';

// Utility for date formatting
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const STATUS_OPTIONS = [
    { value: "not_started", label: "Not started" },
    { value: "in_progress", label: "In progress" },
    { value: "blocked", label: "Blocked" },
    { value: "complete", label: "Complete" }
];

const normalizeStatus = (status) => {
    if (!status) return "not_started";
    const normalized = String(status).trim().toLowerCase();
    if (normalized === "pending" || normalized === "not started" || normalized === "not_started") {
        return "not_started";
    }
    if (normalized === "in progress" || normalized === "in_progress") {
        return "in_progress";
    }
    if (normalized === "blocked") {
        return "blocked";
    }
    if (normalized === "complete" || normalized === "completed") {
        return "complete";
    }
    return "not_started";
};

const getStatusLabel = (status) => {
    const match = STATUS_OPTIONS.find((option) => option.value === status);
    return match ? match.label : "Not started";
};

const getStatusBadgeClasses = (status) => {
    switch (status) {
        case "in_progress":
            return "bg-yellow-500/10 text-yellow-600 dark:text-yellow-300 border-yellow-500/30";
        case "blocked":
            return "bg-yellow-500/10 text-yellow-600 dark:text-yellow-300 border-yellow-500/30";
        case "complete":
            return "bg-green-500/10 text-green-600 dark:text-green-300 border-green-500/30";
        default:
            return "dark:bg-slate-700/40 bg-slate-100 dark:text-slate-300 text-slate-600 border-slate-200 dark:border-slate-600/40";
    }
};

const TaskItem = ({ task, onClick, isSelected, members }) => {
    const statusValue = normalizeStatus(task.status);
    const statusLabel = getStatusLabel(statusValue);
    const isCompleted = statusValue === "complete";
    const owner = members.find(m => m.id === task.ownerId) || { name: task.ownerName || 'Unassigned' };

    // Check if task is overdue (if not completed and due date is in past)
    const isOverdue = !isCompleted && task.dueDate && new Date(task.dueDate) < new Date(new Date().setHours(0, 0, 0, 0));

    return (
        <div
            onClick={() => onClick(task)}
            className={`group flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${isSelected
                ? 'bg-yellow-500/5 dark:bg-yellow-900/20 border-yellow-500/50'
                : 'dark:bg-slate-900 bg-white dark:border-slate-800 border-slate-200 hover:border-yellow-300 dark:hover:border-slate-700'
                }`}
        >
            <div
                className={`flex-shrink-0 px-2 py-1 rounded-full border text-[10px] uppercase tracking-wide ${getStatusBadgeClasses(statusValue)}`}
            >
                {statusLabel}
            </div>

            <div className="flex-grow min-w-0">
                <p className={`text-sm font-medium truncate transition-colors ${isCompleted ? 'text-slate-400 dark:text-slate-500 line-through' : 'dark:text-slate-200 text-slate-700'
                    }`}>
                    {task.title}
                </p>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                    {(task.dueDate || isOverdue) && (
                        <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-400' : ''}`}>
                            <Calendar className="w-3 h-3" />
                            {task.dueDate ? formatDate(task.dueDate) : 'No Date'}
                        </span>
                    )}
                    <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {owner.name}
                    </span>
                </div>
            </div>

            <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <ChevronRight className="w-4 h-4 text-slate-500" />
            </div>
        </div>
    );
};

const TaskDetailPane = ({ task, onClose, onUpdate, onDelete, members, currentUser, onError }) => {
    const [comments, setComments] = useState([]);
    const [isLoadingComments, setIsLoadingComments] = useState(false);
    const [newComment, setNewComment] = useState("");
    const [isPostingComment, setIsPostingComment] = useState(false);

    const [localTask, setLocalTask] = useState(task);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setLocalTask({ ...task, status: normalizeStatus(task.status) });
        loadComments();
    }, [task.id]);

    const loadComments = async () => {
        setIsLoadingComments(true);
        try {
            const res = await fetchTaskComments(task.id);
            const data = Array.isArray(res)
                ? res
                : Array.isArray(res?.data)
                    ? res.data
                    : Array.isArray(res?.data?.data)
                        ? res.data.data
                        : [];
            if (process.env.NODE_ENV !== "production") {
                console.log("[CaseTasks] loadComments", {
                    taskId: task.id,
                    rawResponse: res,
                    parsedCount: data.length
                });
            }
            setComments(data);
        } catch (e) {
            console.error("Failed to load comments", e);
            if (onError) onError("Failed to load comments.");
        } finally {
            setIsLoadingComments(false);
        }
    };

    const handleFieldChange = async (updates) => {
        const updated = { ...localTask, ...updates };
        setLocalTask(updated);

        // Debounce or immediate save? Let's do immediate for now but user might type fast. 
        // For title/desc, maybe onBlur. For dropdowns, immediate.
        // We'll trust the parent to handle the API call and update the main list.
        onUpdate(task.id, updates);
    };

    const handleCommentSubmit = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;

        setIsPostingComment(true);
        try {
            const res = await addTaskComment(task.id, {
                text: newComment,
                author: currentUser?.name || currentUser?.email || 'You'
            });
            if (process.env.NODE_ENV !== "production") {
                console.log("[CaseTasks] addTaskComment", {
                    taskId: task.id,
                    rawResponse: res
                });
            }
            const comment = res?.data?.data || res?.data || res;
            if (comment) {
                setComments(prev => [...prev, comment].filter(Boolean));
            } else {
                await loadComments();
            }
            setNewComment("");
        } catch (e) {
            console.error("Failed to post comment", e);
            if (onError) onError("Failed to post comment.");
        } finally {
            setIsPostingComment(false);
        }
    };

    return (
        <div className="h-full flex flex-col dark:bg-slate-900 bg-white border-l dark:border-slate-800 border-slate-200 w-full md:w-[400px]">
            {/* Header */}
            <div className="flex items-center justify-end p-4 border-b dark:border-slate-800 border-slate-200">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onDelete(task.id)}
                        className="p-2 dark:text-slate-400 text-slate-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        title="Delete Task"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                    <button onClick={onClose} className="p-2 dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {/* Title */}
                <div>
                    <input
                        type="text"
                        value={localTask.title}
                        onChange={(e) => setLocalTask({ ...localTask, title: e.target.value })}
                        onBlur={(e) => {
                            if (e.target.value !== task.title) {
                                handleFieldChange({ title: e.target.value });
                            }
                        }}
                        className="w-full bg-transparent text-lg font-bold dark:text-white text-slate-900 border-none focus:ring-0 p-0 dark:placeholder-slate-600 placeholder-slate-400"
                        placeholder="Task title"
                    />
                </div>

                {/* Meta Fields */}
                <div className="space-y-4">
                    <div className="flex items-center gap-4">
                        <div className="w-24 text-sm text-slate-500 flex items-center gap-2">
                            <Circle className="w-4 h-4" />
                            Status
                        </div>
                        <div className="flex-1">
                            <select
                                value={localTask.status || "not_started"}
                                onChange={(e) => handleFieldChange({ status: e.target.value })}
                                className="w-full dark:bg-slate-800 bg-slate-100 border dark:border-slate-700 border-slate-200 rounded-md text-sm dark:text-slate-200 text-slate-800 focus:ring-yellow-500 focus:border-yellow-500 p-2"
                            >
                                {STATUS_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="w-24 text-sm text-slate-500 flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Assignee
                        </div>
                        <div className="flex-1">
                            <select
                                value={localTask.ownerId || ""}
                                onChange={(e) => {
                                    const m = members.find(m => m.id === e.target.value);
                                    handleFieldChange({
                                        ownerId: e.target.value,
                                        ownerName: m ? m.name : 'Unassigned'
                                    });
                                }}
                                className="w-full dark:bg-slate-800 bg-slate-100 border dark:border-slate-700 border-slate-200 rounded-md text-sm dark:text-slate-200 text-slate-800 focus:ring-yellow-500 focus:border-yellow-500 p-2"
                            >
                                <option value="">Unassigned</option>
                                {members.map(m => (
                                    <option key={m.id} value={m.id}>{m.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="w-24 text-sm text-slate-500 flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            Due Date
                        </div>
                        <div className="flex-1">
                            <input
                                type="date"
                                value={localTask.dueDate ? localTask.dueDate.substring(0, 10) : ""}
                                onChange={(e) => handleFieldChange({ dueDate: e.target.value })}
                                className="w-full dark:bg-slate-800 bg-slate-100 border dark:border-slate-700 border-slate-200 rounded-md text-sm dark:text-slate-200 text-slate-800 focus:ring-yellow-500 focus:border-yellow-500 p-2"
                            />
                        </div>
                    </div>
                </div>

                {/* Comments Section */}
                <div className="pt-6 border-t border-slate-800">
                    <h4 className="text-sm font-semibold text-slate-400 mb-4 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Comments
                    </h4>

                    <div className="space-y-4 mb-4">
                        {isLoadingComments ? (
                            <div className="flex justify-center p-4">
                                <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
                            </div>
                        ) : (comments || []).filter(Boolean).length > 0 ? (
                            (comments || []).filter(Boolean).map((comment, idx) => (
                                <div key={idx} className="flex gap-3">
                                    <div className="w-8 h-8 rounded-full dark:bg-slate-700 bg-slate-200 flex items-center justify-center text-xs font-bold dark:text-slate-300 text-slate-600 flex-shrink-0">
                                        {comment.author ? comment.author.charAt(0).toUpperCase() : 'U'}
                                    </div>
                                    <div>
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-sm font-semibold dark:text-slate-200 text-slate-800">{comment.author || "Unknown"}</span>
                                            <span className="text-xs text-slate-500">
                                                {comment.createdAt ? new Date(comment.createdAt).toLocaleString() : "Just now"}
                                            </span>
                                        </div>
                                        <p className="text-sm dark:text-slate-300 text-slate-600 mt-1">{comment.text || ""}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-sm text-slate-600 italic">No comments yet.</p>
                        )}
                    </div>

                    {/* Add Comment Input */}
                    <div className="flex gap-2">
                        <div className="w-8 h-8 rounded-full bg-yellow-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                            Y
                        </div>
                        <div className="flex-1 relative">
                            <textarea
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleCommentSubmit(e);
                                    }
                                }}
                                placeholder="Write a comment..."
                                className="w-full dark:bg-slate-800 bg-slate-100 border dark:border-slate-700 border-slate-200 rounded-lg p-3 pr-10 text-sm dark:text-slate-200 text-slate-800 focus:ring-yellow-500 focus:border-yellow-500 min-h-[80px]"
                            />
                            <button
                                onClick={handleCommentSubmit}
                                disabled={!newComment.trim() || isPostingComment}
                                className="absolute bottom-3 right-3 p-1.5 bg-yellow-600 hover:bg-yellow-500 disabled:bg-slate-700 text-white rounded-md transition-colors"
                            >
                                <Send className="w-3 h-3" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CaseTasks = ({ caseId, members = [], currentUser }) => {
    const [tasks, setTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTask, setSelectedTask] = useState(null);
    const [deleteCandidateId, setDeleteCandidateId] = useState(null);
    const [deleteCandidateTitle, setDeleteCandidateTitle] = useState("");

    // Celebration Hook
    const { triggerCelebration, celebrationState } = useCelebration();

    // New task state
    const [newTaskTitle, setNewTaskTitle] = useState("");
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        loadTasks();
    }, [caseId]);

    const loadTasks = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await fetchCaseTasks(caseId);
            const data = Array.isArray(res)
                ? res
                : Array.isArray(res?.data)
                    ? res.data
                    : Array.isArray(res?.data?.data)
                        ? res.data.data
                        : [];
            if (process.env.NODE_ENV !== "production") {
                console.log("[CaseTasks] loadTasks", {
                    caseId,
                    rawResponse: res,
                    parsedCount: data.length
                });
            }
            setTasks(data.filter((task) => task && task.id).map((task) => ({
                ...task,
                status: normalizeStatus(task.status)
            })));
        } catch (e) {
            console.error("Failed to load tasks", e);
            setError("Unable to load tasks. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateTask = async (e) => {
        e.preventDefault();
        if (!newTaskTitle.trim()) return;

        setIsCreating(true);
        setError(null);
        try {
            const res = await createTask(caseId, {
                title: newTaskTitle,
                ownerId: currentUser?.id,
                status: "not_started"
            });
            const task = res?.data?.data || res?.data || res;
            if (process.env.NODE_ENV !== "production") {
                console.log("[CaseTasks] createTask", {
                    caseId,
                    rawResponse: res,
                    parsedTask: task
                });
            }
            if (task?.id) {
                setTasks(prev => [{ ...task, status: normalizeStatus(task.status) }, ...prev].filter((item) => item && item.id));
            } else {
                await loadTasks();
            }
            setNewTaskTitle("");
        } catch (e) {
            console.error("Failed to create task", e);

            // Provide specific error messages based on status code
            let errorMessage = "Failed to create task. Please try again.";

            if (e.status === 503) {
                errorMessage = "Service temporarily unavailable. The server is currently down or under maintenance. Please try again in a few minutes.";
            } else if (e.status === 500) {
                errorMessage = "Server error occurred. Please try again later.";
            } else if (e.status === 401 || e.status === 403) {
                errorMessage = "You don't have permission to create tasks.";
            } else if (e.status === 400) {
                errorMessage = "Invalid task data. Please check your input.";
            } else if (!navigator.onLine || e.message?.includes('NetworkError') || e.message?.includes('Failed to fetch')) {
                errorMessage = "No internet connection. Please check your network.";
            }

            setError(errorMessage);
        } finally {
            setIsCreating(false);
        }
    };

    const handleUpdateTask = async (taskId, updates) => {
        setError(null);
        const normalizedUpdates = updates.status
            ? { ...updates, status: normalizeStatus(updates.status) }
            : updates;
        // Optimistic update
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...normalizedUpdates } : t));
        if (selectedTask?.id === taskId) {
            setSelectedTask({ ...selectedTask, ...normalizedUpdates });
        }

        // Check for completion via details pane
        if (normalizedUpdates.status === "complete" && normalizeStatus(selectedTask?.status) !== "complete") {
            triggerCelebration();
        }

        try {
            await updateTask(caseId, taskId, normalizedUpdates);
            if (process.env.NODE_ENV !== "production") {
                console.log("[CaseTasks] updateTask", {
                    caseId,
                    taskId,
                    updates: normalizedUpdates
                });
            }
        } catch (e) {
            console.error("Failed to update task", e);
            setError("Failed to save changes.");
            // Revert on error could go here
        }
    };

    const handleDeleteTask = (taskId) => {
        const task = (tasks || []).find((item) => item?.id === taskId);
        setDeleteCandidateId(taskId);
        setDeleteCandidateTitle(task?.title || "Untitled task");
    };

    const confirmDeleteTask = async () => {
        if (!deleteCandidateId) return;
        setError(null);
        try {
            await deleteTask(caseId, deleteCandidateId);
            if (process.env.NODE_ENV !== "production") {
                console.log("[CaseTasks] deleteTask", { caseId, taskId: deleteCandidateId });
            }
            setTasks(prev => prev.filter(t => t.id !== deleteCandidateId));
            if (selectedTask?.id === deleteCandidateId) {
                setSelectedTask(null);
            }
        } catch (e) {
            console.error("Failed to delete task", e);
            setError("Failed to delete task.");
        } finally {
            setDeleteCandidateId(null);
            setDeleteCandidateTitle("");
        }
    };

    const cancelDeleteTask = () => {
        setDeleteCandidateId(null);
        setDeleteCandidateTitle("");
    };

    const handlePaneError = (msg) => {
        setError(msg);
        // Clear error after 5 seconds
        setTimeout(() => setError(null), 5000);
    };

    return (
        <div className="flex h-[500px] w-full max-w-full border dark:border-slate-800 border-slate-200 rounded-xl dark:bg-slate-900/50 bg-white/50 relative overflow-hidden">

            <CelebrationOverlay state={celebrationState} />

            {/* Task List */}
            <div className={`flex-1 flex flex-col min-w-0 ${selectedTask ? 'hidden md:flex' : 'flex'}`}>
                {/* Header / Add Task */}
                <div className="p-4 border-b dark:border-slate-800 border-slate-200 dark:bg-slate-900/80 bg-white/80 backdrop-blur-sm sticky top-0 z-10 space-y-3">
                    {error && (
                        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-3 py-2 rounded-lg text-sm flex items-center justify-between">
                            <span>{error}</span>
                            <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
                        </div>
                    )}
                    <form onSubmit={handleCreateTask} className="relative">
                        <Plus className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                            type="text"
                            value={newTaskTitle}
                            onChange={(e) => setNewTaskTitle(e.target.value)}
                            placeholder="Add a task..."
                            className="w-full dark:bg-slate-800 bg-slate-100 border dark:border-slate-700 border-slate-200 rounded-lg pl-10 pr-4 py-2.5 text-sm dark:text-slate-200 text-slate-800 dark:placeholder-slate-500 placeholder-slate-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                            disabled={isCreating}
                        />
                        {isCreating && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                <Loader2 className="w-4 h-4 animate-spin text-yellow-500" />
                            </div>
                        )}
                    </form>
                </div>

                {/* List Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-40 text-slate-500 gap-2">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Loading tasks...
                        </div>
                    ) : (tasks || []).length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                            <CheckCircle2 className="w-12 h-12 mb-3 text-slate-700" />
                            <p>No tasks yet.</p>
                            <p className="text-xs text-slate-600">Create one to get started!</p>
                        </div>
                    ) : (
                        // Sort: Incomplete first, then by date? Or just creation.
                        // Let's sort completed to bottom.
                        [...(tasks || [])].filter((task) => task && task.id).sort((a, b) => {
                            const order = {
                                not_started: 0,
                                in_progress: 1,
                                blocked: 2,
                                complete: 3
                            };
                            const aStatus = normalizeStatus(a.status);
                            const bStatus = normalizeStatus(b.status);
                            return (order[aStatus] ?? 0) - (order[bStatus] ?? 0);
                        }).map(task => (
                            <TaskItem
                                key={task.id}
                                task={task}
                                members={members}
                                isSelected={selectedTask?.id === task.id}
                                onClick={setSelectedTask}
                            />
                        ))
                    )}
                </div>
            </div>

            {/* Task Details - Slide in or separate pane */}
            {selectedTask && (
                <div className="absolute inset-0 md:static md:inset-auto z-20 flex dark:bg-slate-900 bg-white md:bg-transparent w-full md:w-auto">
                    <TaskDetailPane
                        task={selectedTask}
                        members={members}
                        currentUser={currentUser}
                        onClose={() => setSelectedTask(null)}
                        onUpdate={handleUpdateTask}
                        onDelete={handleDeleteTask}
                        onError={handlePaneError}
                    />
                </div>
            )}

            {deleteCandidateId && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
                    <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-md shadow-2xl p-6 space-y-4">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Delete Task</p>
                                <p className="text-lg font-bold dark:text-white text-slate-900 break-words">{deleteCandidateTitle}</p>
                                <p className="text-sm dark:text-slate-400 text-slate-600 mt-1">Are you sure you want to delete this task?</p>
                            </div>
                            <button
                                onClick={cancelDeleteTask}
                                className="dark:text-slate-400 text-slate-500 hover:text-slate-900 dark:hover:text-white p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={cancelDeleteTask}
                                className="px-4 py-2 rounded-lg border dark:border-slate-700 border-slate-300 dark:text-slate-300 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteTask}
                                className="px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-500 transition-colors flex items-center gap-2"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CaseTasks;
